import clsx from 'clsx';
import { useGlobalScheduleQuery } from '../../../api/globalScheduleQuery';
import {
    createSavedScheduleSelectionUpdates,
    savedSchedulesGlobalState,
} from '../../../state/localStorage/savedSchedules';
import { useCurrentLocale } from '../../../i18n/useCurrentLocale';
import { anchorPushStateHandler, useURLCreator } from '../../../state/queryParams/manager';
import { Button } from '../../common/Button';
import { RoundIconButton } from '../../common/RoundIconButton';
import { MainViewSidebarSection } from './MainViewSidebarSection';

export const MainViewSidebarSavedSchedulesSection = () => {
    const currentLocale = useCurrentLocale();
    const query = useGlobalScheduleQuery();
    const savedSchedules = savedSchedulesGlobalState.use();

    const activeSavedSchedule = savedSchedules.find((savedSchedule) => {
        const queryHiddenSubjectsEntries = Object.entries(query.params.hiddenSubjects);
        const savedScheduleHiddenSubjectsEntries = Object.entries(savedSchedule.hiddenSubjects);

        return (
            savedSchedule.type === query.params.scheduleType &&
            savedSchedule.headers.length === query.params.scheduleIds.length &&
            savedSchedule.headers.every((header, i) => header.id === query.params.scheduleIds[i]) &&
            savedScheduleHiddenSubjectsEntries.length === queryHiddenSubjectsEntries.length &&
            savedScheduleHiddenSubjectsEntries.every(
                (savedScheduleHiddenSubject, i) =>
                    savedScheduleHiddenSubject[0] === queryHiddenSubjectsEntries[i]![0] &&
                    savedScheduleHiddenSubject[1].length === queryHiddenSubjectsEntries[i]![1]!.length &&
                    savedScheduleHiddenSubject[1].every(
                        (subject, j) => subject === queryHiddenSubjectsEntries[i]![1]![j]!,
                    ),
            )
        );
    });

    const createURL = useURLCreator();

    return (
        <MainViewSidebarSection title={currentLocale.getLabel('main.sidebar.savedSchedules.sectionTitle')}>
            <div class="divide-x-main-bg-4 flex flex-col divide-y-2">
                {savedSchedules.map((savedSchedule, savedScheduleIdx) => {
                    const savedScheduleHiddenSubjectTypeCombosCount = Object.values(
                        savedSchedule.hiddenSubjects,
                    ).reduce((count, value) => count + value.length, 0);
                    const savedScheduleDisplayName = savedSchedule.headers.map((header) => header.name).join(', ');
                    const savedScheduleDisplayNameHiddenSubjectsPart =
                        savedScheduleHiddenSubjectTypeCombosCount > 0
                            ? `+ ${currentLocale.getLabel('main.nHiddenSubjectTypeCombos', {
                                  pluralRulesN: savedScheduleHiddenSubjectTypeCombosCount,
                                  args: [savedScheduleHiddenSubjectTypeCombosCount],
                              })}`
                            : '';

                    return (
                        <div
                            class={clsx(
                                'flex w-full items-center justify-between p-1.25',
                                savedSchedule === activeSavedSchedule && 'bg-x-main-bg-3',
                            )}
                        >
                            <a
                                class="truncate hover:underline"
                                href={createURL(...createSavedScheduleSelectionUpdates(savedSchedule))}
                                onClick={anchorPushStateHandler}
                                title={currentLocale.getLabel('main.sidebar.savedSchedules.selectXCTA', {
                                    args: [savedScheduleDisplayName],
                                })}
                            >
                                <span>{savedScheduleDisplayName}</span>

                                {savedScheduleDisplayNameHiddenSubjectsPart && (
                                    <span class="text-xs"> {savedScheduleDisplayNameHiddenSubjectsPart}</span>
                                )}
                            </a>

                            <div class="text-x-main-text-muted flex">
                                <div class="flex flex-col items-center justify-center">
                                    <RoundIconButton
                                        class="h-5 rotate-180 p-0.5"
                                        icon="chevronDown"
                                        title={currentLocale.getLabel('main.sidebar.savedSchedules.moveBackXCTA', {
                                            args: [savedScheduleDisplayName],
                                        })}
                                        disabled={savedScheduleIdx === 0}
                                        onClick={() => savedSchedulesGlobalState.moveBack(savedSchedule)}
                                    />
                                    <RoundIconButton
                                        class="h-5 p-0.5"
                                        icon="chevronDown"
                                        title={currentLocale.getLabel('main.sidebar.savedSchedules.moveForwardXCTA', {
                                            args: [savedScheduleDisplayName],
                                        })}
                                        disabled={savedScheduleIdx === savedSchedules.length - 1}
                                        onClick={() => savedSchedulesGlobalState.moveForward(savedSchedule)}
                                    />
                                </div>
                                <RoundIconButton
                                    class="h-8 p-1.25"
                                    icon="cross"
                                    title={currentLocale.getLabel('main.sidebar.savedSchedules.removeXCTA', {
                                        args: [savedScheduleDisplayName],
                                    })}
                                    onClick={() => {
                                        if (
                                            window.confirm(
                                                currentLocale.getLabel(
                                                    'main.sidebar.savedSchedules.confirmPromptMessage',
                                                    {
                                                        args: [
                                                            savedScheduleDisplayNameHiddenSubjectsPart
                                                                ? `${savedScheduleDisplayName} (${savedScheduleDisplayNameHiddenSubjectsPart})`
                                                                : savedScheduleDisplayName,
                                                        ],
                                                    },
                                                ),
                                            )
                                        ) {
                                            savedSchedulesGlobalState.remove(savedSchedule);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div class="mt-2 flex gap-2">
                {query.params.scheduleIds.length > 0 && (
                    <Button
                        type="button"
                        disabled={!query.data || !!activeSavedSchedule}
                        text={currentLocale.getLabel('main.sidebar.savedSchedules.saveCTAShort')}
                        title={currentLocale.getLabel('main.sidebar.savedSchedules.saveCTA')}
                        icon="save"
                        onClick={() =>
                            savedSchedulesGlobalState.add({
                                type: query.params.scheduleType,
                                headers: query.data!.resource.aggregateSchedule.headers,
                                hiddenSubjects: query.params.hiddenSubjects,
                            })
                        }
                    />
                )}
            </div>
        </MainViewSidebarSection>
    );
};
